import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { signIn, signUp, signOut, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { resolveRole } from '../../utils/authRole';

// AWS Amplify authentication integration.
// Roles come from the signed ID token's cognito:groups claim -- the same claim
// AppSync enforces -- not from a list of emails in this bundle. See utils/authRole.

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // Sign in with AWS Cognito
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: password,
      });
      
      if (isSignedIn) {
        // Get current user and attributes
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        const { role, groups } = await resolveRole();
        
        return {
          user: {
            id: user.userId,
            username: user.username,
            email: attributes.email,
            name: attributes.name || attributes.email,
            phone: attributes.phone_number,
            role: role,
            groups: groups,
          },
          token: 'cognito-authenticated',
        };
      } else {
        throw new Error('Sign in not completed');
      }
    } catch (error) {
      console.error('Login error:', error);
      return rejectWithValue(error.message || 'Authentication failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password, name, phone }, { rejectWithValue }) => {
    try {
      // Sign up with AWS Cognito
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
            phone_number: phone,
          },
        },
      });
      
      return {
        username: userId,
        email,
        name,
        needsConfirmation: !isSignUpComplete,
      };
    } catch (error) {
      console.error('Registration error:', error);
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await signOut();
      return {};
    } catch (error) {
      console.error('Logout error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      
      const { role, groups } = await resolveRole();
      
      return {
        user: {
          id: user.userId,
          username: user.username,
          email: attributes.email,
          name: attributes.name || attributes.email,
          phone: attributes.phone_number,
          role: role,
          groups: groups,
        },
        token: 'cognito-authenticated',
      };
    } catch (error) {
      return rejectWithValue('Not authenticated');
    }
  }
);

// Load authentication state from localStorage
const loadAuthStateFromStorage = () => {
  try {
    const storedAuth = localStorage.getItem('authState');
    if (storedAuth) {
      return JSON.parse(storedAuth);
    }
  } catch (error) {
    console.warn('Failed to load auth state from localStorage:', error);
    localStorage.removeItem('authState');
  }
  return {
    user: null,
    token: null,
    isAuthenticated: false,
  };
};

// Save authentication state to localStorage
const saveAuthStateToStorage = (user, token, isAuthenticated) => {
  try {
    const authState = { user, token, isAuthenticated };
    localStorage.setItem('authState', JSON.stringify(authState));
  } catch (error) {
    console.warn('Failed to save auth state to localStorage:', error);
  }
};

// Clear authentication state from localStorage
const clearAuthStateFromStorage = () => {
  try {
    localStorage.removeItem('authState');
  } catch (error) {
    console.warn('Failed to clear auth state from localStorage:', error);
  }
};

const storedAuthState = loadAuthStateFromStorage();

const initialState = {
  user: storedAuthState.user,
  token: storedAuthState.token,
  isAuthenticated: storedAuthState.isAuthenticated,
  loading: true, // Start as true to prevent redirect during initial auth check
  error: null,
  needsConfirmation: false,
  registrationData: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRegistrationData: (state) => {
      state.registrationData = null;
      state.needsConfirmation = false;
    },
    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        // Save to localStorage
        saveAuthStateToStorage(action.payload.user, action.payload.token, true);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        // Clear localStorage
        clearAuthStateFromStorage();
      })
      
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.needsConfirmation = action.payload.needsConfirmation;
        state.registrationData = action.payload;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        // Clear localStorage
        clearAuthStateFromStorage();
      })
      
      // Check auth status
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        // Save to localStorage
        saveAuthStateToStorage(action.payload.user, action.payload.token, true);
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        // Clear localStorage
        clearAuthStateFromStorage();
      });
  },
});

export const { clearError, clearRegistrationData, updateUserProfile } = authSlice.actions;
export default authSlice.reducer;